/*---------------------------------------------------------------------------------------------
 * Copyright (c) Bentley Systems, Incorporated. All rights reserved.
 * See LICENSE.md in the project root for license terms and full copyright notice.
 *--------------------------------------------------------------------------------------------*/
/** @packageDocumentation
 * @module Backstage
 */

import type { CommonProps } from "@itwin/core-react";
import classnames from "classnames";
import * as React from "react";
import { Key } from "ts-key-enum";
import type { SafeAreaInsets } from "../../safearea/SafeAreaInsets.js";
import { SafeAreaInsetsHelpers } from "../base/SafeAreaInsets.js";
import "./Backstage.scss";

/** Properties of [[Backstage]] component.
 * @internal
 */
// eslint-disable-next-line @typescript-eslint/no-deprecated
export interface BackstageProps extends CommonProps {
  /** Backstage items and separators. I.e. [[BackstageItem]], [[BackstageSeparator]] */
  children?: React.ReactNode;
  /** Optional footer content. */
  footer?: React.ReactNode;
  /** Optional header content. I.e. [[UserProfile]] */
  header?: React.ReactNode;
  /** Describes if the backstage is open. */
  isOpen?: boolean;
  /** Function called when backstage is closed. */
  onClose?: () => void;
  /** Describes respected safe area insets. */
  safeAreaInsets?: SafeAreaInsets;
  /** Describes if a ghosting overlay is shown. */
  showOverlay: boolean;
}

/** Default properties of [[Backstage]] component.
 * @internal
 */
export type BackstageDefaultProps = Pick<BackstageProps, "showOverlay">;

/** Backstage component of 9-Zone UI app.
 * @internal
 */
export class Backstage extends React.PureComponent<BackstageProps> {
  public static readonly defaultProps: BackstageDefaultProps = {
    showOverlay: true,
  };

  public override componentDidMount(): void {
    document.addEventListener("keydown", this._onEsc, false);
  }

  public override componentWillUnmount() {
    document.removeEventListener("keydown", this._onEsc, false);
  }

  public override render() {
    const overlayClassName = classnames(
      "nz-backstage-backstage_overlay",
      this.props.isOpen && "nz-open",
      this.props.showOverlay && "nz-overlay"
    );
    const backstageClassName = classnames(
      "nz-backstage-backstage",
      this.props.isOpen && "nz-open",
      this.props.safeAreaInsets &&
        SafeAreaInsetsHelpers.getCssClassNames(this.props.safeAreaInsets),
      this.props.className
    );
    return (
      <>
        <div
          className={overlayClassName}
          onClick={this._onClose}
          role="presentation"
        />
        <div className={backstageClassName} style={this.props.style}>
          {this.props.header && (
            <div className="nz-header">{this.props.header}</div>
          )}
          <ul role="menu">{this.props.children}</ul>
          {this.props.footer && (
            <div className="nz-footer">{this.props.footer}</div>
          )}
        </div>
      </>
    );
  }

  private _onClose = () => {
    this.props.onClose && this.props.onClose();
  };

  private _onEsc = (event: KeyboardEvent): void => {
    if (this.props.isOpen && event.key === Key.Escape.valueOf()) {
      this._onClose();
    }
  };
}
